import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import type { Location } from 'react-router-dom'
import type { ReactElement } from 'react'
import Layout from './components/Layout'
import { Spinner } from './components/ui'
import LearnPage from './pages/Learn'
import LevelSelectPage from './pages/LevelSelect'
import LoginPage from './pages/Login'
import MapPage from './pages/MapPage'
import ProfilePage from './pages/Profile'
import QuizPage from './pages/Quiz'
import ScorecardPage from './pages/Scorecard'
import ScoreboardPage from './pages/Scoreboard'
import { useAuth } from './store/auth'

/** Routes that need an account; anonymous visitors are sent to sign in. */
function RequireAuth({ children }: { children: ReactElement }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) {
    return (
      <div className="grid h-full place-items-center p-10">
        <Spinner label="Restoring your session…" />
      </div>
    )
  }
  // Carry the attempted URL through the bounce. Map links encode level, selection
  // and focus in the query string, so dropping it would lose a shared deep link.
  return user ? children : <Navigate to="/login" replace state={{ from: location }} />
}

export default function App() {
  const { user, loading } = useAuth()
  const location = useLocation()

  const from = (location.state as { from?: Location } | null)?.from
  const afterLogin = from ? `${from.pathname}${from.search}${from.hash}` : '/map'

  return (
    <Routes>
      <Route
        path="/login"
        element={loading ? <div className="grid h-full place-items-center"><Spinner /></div>
                         : user ? <Navigate to={afterLogin} replace /> : <LoginPage />}
      />
      <Route element={<Layout />}>
        {/* Everything behind the nav requires an account. The leaderboard stays open
            so a signed-out visitor can still see the board they would be joining. */}
        <Route path="/map" element={<RequireAuth><MapPage /></RequireAuth>} />
        <Route path="/board" element={<ScoreboardPage />} />
        <Route path="/levels" element={<RequireAuth><LevelSelectPage /></RequireAuth>} />
        <Route path="/learn" element={<RequireAuth><LearnPage /></RequireAuth>} />
        <Route path="/quiz" element={<RequireAuth><QuizPage /></RequireAuth>} />
        <Route path="/scores" element={<RequireAuth><ScorecardPage /></RequireAuth>} />
        <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
      </Route>
      <Route path="/" element={<Navigate to="/map" replace />} />
      <Route path="*" element={<Navigate to="/map" replace />} />
    </Routes>
  )
}
